import * as Ariakit from '@ariakit/react';
import React from 'react';

import type { DynamicIconName } from '../../assets/DynamicIcon/DynamicIcon';
import type { SummaryListPairsProps } from '../../components/SummaryList/SummaryList';
import { DynamicIcon } from '../../assets/DynamicIcon/DynamicIcon';
import { SummaryList } from '../../components/SummaryList/SummaryList';
import { Button } from '../../controls/Button/Button';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';
import { Body } from '../../text/Body';
import { space } from '../../utilities/shared/sizes';

const HoverCardIcon = styled(DynamicIcon, {
  color: colors.iconNeutralLight,

  [darkThemeSelector]: {
    color: colors.iconNeutralDark,
  },
});

const HoverCardHeading = styled(Body, {
  fontWeight: '$bold',
});

const HoverCardStart = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$6',
});

const HoverCardHeader = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '$8',
});

const HoverCardContainer = styled(Ariakit.Hovercard, {
  display: 'flex',
  flexDirection: 'column',
  gap: '$12',
  padding: '$12 $16',
  minWidth: '$240',
  overflow: 'auto',
  background: colors.bgApplicationLight,
  borderRadius: '$10',
  boxShadow: shadows.overlayLight,

  [darkThemeSelector]: {
    background: colors.bgApplicationDark,
    boxShadow: shadows.overlayDark,
  },
});

const HoverCardRender = styled('div');

const HoverCardAnchor = styled(Ariakit.HovercardAnchor);

const HoverCardProvider = styled(Ariakit.HovercardProvider);

export type HoverCardProps = {
  /**
   * The trigger for the hover card.
   */
  children: React.ReactNode;
  /**
   * The content of the hover card.
   */
  content?: React.ReactNode;
  /**
   * The heading of the hover card.
   */
  heading: React.ReactNode;
  /**
   * The icon of the hover card.
   */
  icon?: DynamicIconName;
  /**
   * The link to the object the hover card describes.
   */
  link?: {
    /**
     * The component to render the link as.
     */
    as?: React.ElementType;
    /**
     * The to of the link.
     */
    to?: any;
  };
  /**
   * Whether the hover card is open.
   */
  open?: boolean;
  /**
   * The summary list pairs of the hover card.
   */
  summary?: SummaryListPairsProps[];
};

export function HoverCard({
  children,
  content,
  heading,
  icon,
  link,
  open,
  summary,
}: HoverCardProps) {
  return (
    <HoverCardProvider>
      <HoverCardAnchor render={<HoverCardRender />} focusable>
        {children}
      </HoverCardAnchor>
      <HoverCardContainer
        gutter={8}
        fitViewport
        flip
        overflowPadding={12}
        portal
        open={open}
        css={{
          maxWidth: `var(--popover-available-width)`,
          maxHeight: `var(--popover-available-height)`,
        }}
      >
        <HoverCardHeader>
          <HoverCardStart>
            {icon && <HoverCardIcon icon={icon} size={space(14)} />}
            {heading && <HoverCardHeading>{heading}</HoverCardHeading>}
          </HoverCardStart>
          {link && (
            <Button
              as={link.as}
              to={link.to}
              arrangement="hidden-label"
              icon="chevron-right"
              size="small"
              variant="secondary"
            >
              Go to {heading}
            </Button>
          )}
        </HoverCardHeader>
        {summary && <SummaryList gutter="none" pairs={summary} />}
        {content}
      </HoverCardContainer>
    </HoverCardProvider>
  );
}
